import { makeAutoObservable, flow } from 'mobx';
import { notification } from 'antd';
import api from '../../constants/api';
import { axios } from '../http/axios.js';

class RaffleService {
    rootStore = null;

    constructor(rootStore = null) {
        this.rootStore = rootStore;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    setRootStore(rootStore) {
        this.rootStore = rootStore;
    }

    getCurrentUserId() {
        return this.rootStore?.authStore?.me?.id || null;
    }

    // Create a new raffle
    createRaffle = flow(function* (raffleData) {
        try {
            const formData = new FormData();
            
            // Add all form fields
            Object.keys(raffleData).forEach(key => {
                if (key === 'thumbnail' && raffleData[key]?.originFileObj) {
                    formData.append('thumbnail', raffleData[key].originFileObj);
                } else if (key === 'thumbnail' && raffleData[key]?.croppedFileObj) {
                    formData.append('thumbnail', raffleData[key].croppedFileObj);
                } else if (key === 'newGalleryFiles' && Array.isArray(raffleData[key])) {
                    // Handle new gallery files
                    raffleData[key].forEach((image, index) => {
                        if (image?.originFileObj) {
                            formData.append(`prizeImage${index}`, image.originFileObj);
                        } else if (image?.croppedFileObj) {
                            formData.append(`prizeImage${index}`, image.croppedFileObj);
                        }
                    });
                } else if (key === 'prizeImages' && Array.isArray(raffleData[key])) {
                    // Legacy support - handle old prizeImages field
                    raffleData[key].forEach((image, index) => {
                        if (image?.originFileObj) {
                            formData.append(`prizeImage${index}`, image.originFileObj);
                        } else if (image?.croppedFileObj) {
                            formData.append(`prizeImage${index}`, image.croppedFileObj);
                        }
                    });
                } else if (raffleData[key] !== null && raffleData[key] !== undefined && 
                          key !== 'thumbnail' && key !== 'prizeImages' && key !== 'newGalleryFiles') {
                    formData.append(key, typeof raffleData[key] === 'object' ? JSON.stringify(raffleData[key]) : raffleData[key]);
                }
            });

            const result = yield axios.post(api.raffles.createRaffle, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            return result.data;
        } catch (error) {
            console.error('Error creating raffle:', error);
            notification.error({
                message: 'Failed to create raffle',
                description: error.response?.data?.message || error.message || 'Something went wrong',
            });
            throw error;
        }
    });

    // Get raffles for current creator
    getRafflesByCreator = async () => {
        try {
            const result = await axios.get(api.raffles.getUserRaffles);
            return result.data;
        } catch (error) {
            console.error('Error fetching creator raffles:', error);
            notification.error({
                message: 'Failed to fetch raffles',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    }

    // Archive/unarchive raffle
    archiveRaffle = async (raffleId, isArchived = true) => {
        try {
            const result = await axios.patch(api.raffles.archiveRaffle(raffleId), {
                isArchived: isArchived
            });
            
            notification.success({
                message: `Raffle ${isArchived ? 'archived' : 'unarchived'} successfully!`,
            });
            
            return result.data;
        } catch (error) {
            console.error('Error archiving raffle:', error);
            notification.error({
                message: `Failed to ${isArchived ? 'archive' : 'unarchive'} raffle`,
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    }

    // Get single raffle
    getRaffle = flow(function* (raffleId) {
        try {
            const result = yield axios.get(api.raffles.getRaffle(raffleId));
            return result.data;
        } catch (error) {
            console.error('Error fetching raffle:', error);
            notification.error({
                message: 'Failed to fetch raffle',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    });

    // Get raffle by product ID
    getRaffleByProductId = flow(function* (productId) {
        try {
            const result = yield axios.get(api.raffles.getRaffleByProduct(productId));
            return result.data;
        } catch (error) {
            console.error('Error fetching raffle by product ID:', error);
            notification.error({
                message: 'Failed to fetch raffle',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    });

    // Get raffle preview for checkout page (no auth required)
    getRafflePreview = flow(function* (storeName, productId) {
        try {
            const result = yield axios.get(api.raffles.getRafflePreview(storeName, productId));
            return result.data;
        } catch (error) {
            console.error('Error fetching raffle preview:', error);
            throw error;
        }
    });

    // Update raffle
    updateRaffle = flow(function* (raffleId, raffleData) {
        try {
            const formData = new FormData();
            
            // Add all form fields
            Object.keys(raffleData).forEach(key => {
                if (key === 'thumbnail' && raffleData[key]?.originFileObj) {
                    formData.append('thumbnail', raffleData[key].originFileObj);
                } else if (key === 'thumbnail' && raffleData[key]?.croppedFileObj) {
                    formData.append('thumbnail', raffleData[key].croppedFileObj);
                } else if (key === 'newGalleryFiles' && Array.isArray(raffleData[key])) {
                    // Handle new gallery files
                    raffleData[key].forEach((image, index) => {
                        if (image?.originFileObj) {
                            formData.append(`prizeImage${index}`, image.originFileObj);
                        } else if (image?.croppedFileObj) {
                            formData.append(`prizeImage${index}`, image.croppedFileObj);
                        }
                    });
                } else if (key === 'prizeImages' && Array.isArray(raffleData[key])) {
                    // Legacy support - handle old prizeImages field
                    raffleData[key].forEach((image, index) => {
                        if (image?.originFileObj) {
                            formData.append(`prizeImage${index}`, image.originFileObj);
                        } else if (image?.croppedFileObj) {
                            formData.append(`prizeImage${index}`, image.croppedFileObj);
                        }
                    });
                } else if (raffleData[key] !== null && raffleData[key] !== undefined && 
                          key !== 'thumbnail' && key !== 'prizeImages' && key !== 'newGalleryFiles') {
                    formData.append(key, typeof raffleData[key] === 'object' ? JSON.stringify(raffleData[key]) : raffleData[key]);
                }
            });

            const result = yield axios.put(api.raffles.updateRaffle(raffleId), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            notification.success({
                message: 'Raffle updated successfully!',
            });
            
            return result.data;
        } catch (error) {
            console.error('Error updating raffle:', error);
            notification.error({
                message: 'Failed to update raffle',
                description: error.response?.data?.message || error.message || 'Something went wrong',
            });
            throw error;
        }
    });

    // Delete raffle
    deleteRaffle = flow(function* (raffleId) {
        try {
            yield axios.delete(api.raffles.deleteRaffle(raffleId));
            notification.success({
                message: 'Raffle deleted successfully',
            });
        } catch (error) {
            console.error('Error deleting raffle:', error);
            notification.error({
                message: 'Failed to delete raffle',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    });

    // Status management
    publishRaffle = flow(function* (raffleId) {
        try {
            const result = yield axios.post(api.raffles.publishRaffle(raffleId));
            notification.success({
                message: 'Raffle published successfully!',
                description: 'Your raffle is now live and accepting entries.',
            });
            return result.data;
        } catch (error) {
            console.error('Error publishing raffle:', error);
            notification.error({
                message: 'Failed to publish raffle',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    });

    startRaffle = flow(function* (raffleId) {
        try {
            const result = yield axios.post(api.raffles.startRaffle(raffleId));
            notification.success({
                message: 'Raffle started successfully!',
            });
            return result.data;
        } catch (error) {
            console.error('Error starting raffle:', error);
            notification.error({
                message: 'Failed to start raffle',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    });

    endRaffle = flow(function* (raffleId) {
        try {
            const result = yield axios.post(api.raffles.endRaffle(raffleId));
            notification.success({
                message: 'Raffle ended successfully!',
            });
            return result.data;
        } catch (error) {
            console.error('Error ending raffle:', error);
            notification.error({
                message: 'Failed to end raffle',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    });

    drawWinner = flow(function* (raffleId) {
        try {
            const result = yield axios.post(api.raffles.drawWinner(raffleId));
            notification.success({
                message: 'Winner drawn successfully!',
                description: 'The raffle winner has been selected and notified.',
            });
            return result.data;
        } catch (error) {
            console.error('Error drawing winner:', error);
            notification.error({
                message: 'Failed to draw winner',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    });

    // Get raffle analytics
    getRaffleAnalytics = flow(function* (raffleId) {
        try {
            const result = yield axios.get(api.raffles.getRaffleAnalytics(raffleId));
            return result.data;
        } catch (error) {
            console.error('Error fetching raffle analytics:', error);
            notification.error({
                message: 'Failed to fetch analytics',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    });

    // Get raffle tickets
    getRaffleTickets = flow(function* (raffleId) {
        try {
            const result = yield axios.get(api.raffles.getRaffleTickets(raffleId));
            return result.data;
        } catch (error) {
            console.error('Error fetching raffle tickets:', error);
            notification.error({
                message: 'Failed to fetch tickets',
                description: error.response?.data?.message || 'Something went wrong',
            });
            throw error;
        }
    });

    // Public raffle methods
    getPublicRaffles = flow(function* () {
        try {
            const result = yield axios.get(api.raffles.getPublicRaffles);
            return result.data;
        } catch (error) {
            console.error('Error fetching public raffles:', error);
            throw error;
        }
    });

    getPublicRaffle = flow(function* (raffleId) {
        try {
            const result = yield axios.get(api.raffles.getPublicRaffle(raffleId));
            return result.data;
        } catch (error) {
            console.error('Error fetching public raffle:', error);
            throw error;
        }
    });
}

// Create instance without rootStore initially
const raffleService = new RaffleService();
export default raffleService;
